import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import _ from 'lodash';

const FLAG_SIZE = 10;
const OVERLAY_BOTTOM_MARGIN = 5;
const BADGE_HEIGHT = 11;
const BADGE_TOP_GAP = 1;
const BADGE_GAP = 2;
const flagBitmapPromises = new Map();
const resolvedFlagBitmaps = new Map();

function normalizeCountryCode(country) {
  let code = (country || 'unknown').toLowerCase();
  if (code === 'uk') code = 'gb';
  return code;
}

function createFallbackFlagBitmap() {
  const canvas = document.createElement('canvas');
  canvas.width = FLAG_SIZE;
  canvas.height = FLAG_SIZE;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#777';
  ctx.fillRect(0, 0, FLAG_SIZE, FLAG_SIZE);
  return canvas;
}

function loadFlagBitmap(countryCode) {
  const normalized = normalizeCountryCode(countryCode);
  const cacheKey = `${normalized}@${FLAG_SIZE}`;

  if (resolvedFlagBitmaps.has(cacheKey)) {
    return Promise.resolve(resolvedFlagBitmaps.get(cacheKey));
  }

  if (!flagBitmapPromises.has(cacheKey)) {
    const promise = (async () => {
      if (normalized === '*' || normalized === 'unknown') {
        return createFallbackFlagBitmap();
      }

      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = `https://static.opinautos.com/images/banderas/${normalized}.png`;
        await img.decode();

        const canvas = document.createElement('canvas');
        canvas.width = FLAG_SIZE;
        canvas.height = FLAG_SIZE;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, FLAG_SIZE, FLAG_SIZE);
        return canvas;
      } catch {
        return createFallbackFlagBitmap();
      }
    })();

    flagBitmapPromises.set(cacheKey, promise);
  }

  return flagBitmapPromises.get(cacheKey).then(bitmap => {
    resolvedFlagBitmaps.set(cacheKey, bitmap);
    return bitmap;
  });
}

function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function countToBadgeColors(count, maxCount) {
  const gray = [136, 136, 136];
  const yellow = [255, 193, 7];
  const red = [220, 53, 69];

  if (!maxCount) {
    return { bg: `rgba(${gray.join(', ')}, 0.9)`, text: '#fff' };
  }

  const t = count / maxCount;
  let rgb;
  if (t <= 0.5) {
    const u = t * 2;
    rgb = gray.map((c, i) => Math.round(c + (yellow[i] - c) * u));
  } else {
    const u = (t - 0.5) * 2;
    rgb = yellow.map((c, i) => Math.round(c + (red[i] - c) * u));
  }

  const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  return {
    bg: `rgba(${rgb.join(', ')}, 0.9)`,
    text: luminance > 0.62 ? '#333' : '#fff',
  };
}

function drawYearBadge(ctx, centerX, y, count, maxCount) {
  const text = String(count);
  ctx.font = 'bold 8px sans-serif';
  const paddingX = 3;
  const textWidth = ctx.measureText(text).width;
  const badgeW = Math.max(textWidth + paddingX * 2, 12);
  const badgeX = centerX - badgeW / 2;
  const { bg, text: textColor } = countToBadgeColors(count, maxCount);

  roundRect(ctx, badgeX, y, badgeW, BADGE_HEIGHT, 3);
  ctx.fillStyle = bg;
  ctx.fill();

  ctx.fillStyle = textColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, centerX, y + BADGE_HEIGHT / 2);
}

/**
 * Canvas overlay for ModelGenerationsTimeline: year separators, count badges, and vehicle flags.
 *
 * @param {{ vehicles: { year: number, country: string }[], years: number[], tableRef: React.RefObject<HTMLTableElement> }} props
 */
export default function ModelGenerationsTimelineOverlay({ vehicles, years, tableRef }) {
  const canvasRef = useRef(null);
  const [flagsVersion, setFlagsVersion] = useState(0);
  const yearSet = useMemo(() => new Set(years), [years]);

  const vehiclesByCountry = useMemo(() => {
    return _.groupBy(
      vehicles.filter(v => v.year && yearSet.has(v.year)),
      v => normalizeCountryCode(v.country)
    );
  }, [vehicles, yearSet]);

  const countsByYear = useMemo(() => {
    const counts = {};
    for (const v of vehicles) {
      if (v.year && yearSet.has(v.year)) {
        counts[v.year] = (counts[v.year] || 0) + 1;
      }
    }
    return counts;
  }, [vehicles, yearSet]);

  const maxCount = useMemo(() => {
    const values = Object.values(countsByYear);
    return values.length ? Math.max(...values) : 0;
  }, [countsByYear]);

  useEffect(() => {
    let cancelled = false;
    const countries = Object.keys(vehiclesByCountry);
    if (!countries.length) return;

    Promise.all(countries.map(loadFlagBitmap)).then(() => {
      if (!cancelled) setFlagsVersion(v => v + 1);
    });

    return () => { cancelled = true; };
  }, [vehiclesByCountry]);

  const draw = useCallback(() => {
    const table = tableRef.current;
    const canvas = canvasRef.current;
    if (!table || !canvas) return;

    const rect = table.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);

    const headerRow = table.querySelector('thead tr');
    const theadHeight = headerRow?.getBoundingClientRect().height || 0;
    const badgeTop = theadHeight + BADGE_TOP_GAP;
    const drawTop = badgeTop + BADGE_HEIGHT + BADGE_GAP;
    const drawBottom = rect.height - OVERLAY_BOTTOM_MARGIN;
    const drawHeight = drawBottom - drawTop;
    if (drawHeight <= FLAG_SIZE) return;

    const ths = headerRow?.querySelectorAll('th') || [];
    const yearCols = new Map();
    ths.forEach((th, i) => {
      const thRect = th.getBoundingClientRect();
      yearCols.set(years[i], { left: thRect.left - rect.left, width: thRect.width });
    });

    ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    for (const [year, col] of yearCols) {
      if (!countsByYear[year]) continue;
      ctx.fillRect(col.left, 0, col.width, rect.height);
    }

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    for (const [, col] of yearCols) {
      const x = col.left + col.width;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, badgeTop);
      ctx.lineTo(x + 0.5, rect.height);
      ctx.stroke();
    }

    for (const [year, col] of yearCols) {
      const count = countsByYear[year];
      if (!count) continue;
      drawYearBadge(ctx, col.left + col.width / 2, badgeTop, count, maxCount);
    }

    let dotIndex = 0;

    for (const [country, countryVehicles] of Object.entries(vehiclesByCountry)) {
      const flag = resolvedFlagBitmaps.get(`${country}@${FLAG_SIZE}`);
      if (!flag) continue;

      for (const v of countryVehicles) {
        const col = yearCols.get(v.year);
        if (!col || col.width <= 0) continue;

        const seed = dotIndex * 9973 + v.year * 13 + country.charCodeAt(0);
        dotIndex++;
        const x = col.left + 2 + seededRandom(seed) * Math.max(col.width - FLAG_SIZE - 4, 1);
        const y = drawTop + seededRandom(seed + 1) * Math.max(drawHeight - FLAG_SIZE, 1);

        ctx.drawImage(flag, x, y, FLAG_SIZE, FLAG_SIZE);
      }
    }
  }, [vehiclesByCountry, countsByYear, maxCount, years, tableRef, flagsVersion]);

  useLayoutEffect(() => {
    draw();
    const table = tableRef.current;
    if (!table) return;

    const observer = new ResizeObserver(draw);
    observer.observe(table);
    return () => observer.disconnect();
  }, [draw, tableRef]);

  return (
    <canvas
      ref={canvasRef}
      className='position-absolute'
      style={{ top: 0, left: 0, pointerEvents: 'none', zIndex: 1 }}
    />
  );
}
